import asyncRetry from 'async/retry';
import util from './util';
import Storage from './storage';
import Platform from './platform';

export default {
  init,
  isReady,
  getCustomBaseUrl,
  setCustomBaseUrl,
  getBaseUrl,
  get,
  post,
  put,
  del,
  cdnGet,
  request,
  rawRequest,
  newWebSocket,
};

const DEFAULT_TIMEOUT = 10 * 1000;
const MAX_RETRY_INTERVAL = 60 * 1000;
const CUSTOM_BASE_URL_KEY = 'ARES_ADMIN_CUSTOM_BASE_URL';

const DEV_BASE_URL = 'https://api-dev.ares-dev.com';
const DEV_BASE_CDN_URL = 'https://api-dev-cdn.ares-dev.com';
const STAGING_BASE_URL = 'https://api-dev.ares-dev.com';
const STAGING_BASE_CDN_URL = 'https://api-dev-cdn.ares-dev.com';
const PROD_BASE_URL = 'https://api-dev.ares-dev.com';
const PROD_BASE_CDN_URL = 'https://api-dev-cdn.ares-dev.com';

let g_base_url = '';
let g_base_cdn_url = '';
let g_custom_base_url = '';
let g_user_agent = '';

function init(done: any): void {
  const env = util.getEnvironment();
  if (env === 'dev') {
    g_base_url = DEV_BASE_URL;
    g_base_cdn_url = DEV_BASE_CDN_URL;
    util.log('Api.init: set dev urls:', g_base_url);
  } else if (env === 'staging') {
    g_base_url = STAGING_BASE_URL;
    g_base_cdn_url = STAGING_BASE_CDN_URL;
    util.log('Api.init: set staging urls:', g_base_url);
  } else {
    g_base_url = PROD_BASE_URL;
    g_base_cdn_url = PROD_BASE_CDN_URL;
  }

  if (Platform && Platform.RNInfoManifestBridge) {
    const { RNInfoManifestBridge } = Platform;
    const ver = RNInfoManifestBridge.shortVersion;
    const os = Platform.OS === 'ios' ? 'ios' : 'android';
    const os_ver =
      Platform.OS === 'ios'
        ? RNInfoManifestBridge.iosVersion
        : RNInfoManifestBridge.androidVersion;
    const device_type = _clean(
      Platform.OS === 'ios'
        ? RNInfoManifestBridge.iosSystemInfoMachine
        : RNInfoManifestBridge.androidDeviceName
    );
    const device_family = _clean(
      Platform.OS === 'ios' ? RNInfoManifestBridge.iosDeviceFamily : 'android'
    ).toLowerCase();
    const language = RNInfoManifestBridge.language;
    g_user_agent = `ares/${ver} os/${os} os_ver/${os_ver} device_type/${device_type} device_family/${device_family} language/${language}`;
  }

  Storage.getItem(CUSTOM_BASE_URL_KEY, (err, value) => {
    if (!err && value) {
      util.log('Api.init: custom base url:', value);
      g_custom_base_url = value;
    }
    done();
  });
}

function isReady(): boolean {
  return true;
}
function getCustomBaseUrl(): string {
  return g_custom_base_url;
}
function setCustomBaseUrl(url?: string): void {
  if (!url) {
    g_custom_base_url = '';
  } else {
    url = url.replace(/\/*$/, '');
    g_custom_base_url = url;
  }
  Storage.setItem({ key: CUSTOM_BASE_URL_KEY, value: g_custom_base_url });
}
if (window) {
  (window as any).getCustomBaseUrl = getCustomBaseUrl;
  (window as any).setCustomBaseUrl = setCustomBaseUrl;
}

function getBaseUrl(): string {
  return g_custom_base_url ? g_custom_base_url : g_base_url;
}
function getCdnUrl(): string {
  return g_custom_base_url ? g_custom_base_url : g_base_cdn_url;
}

function get(opts: any, done: any): void {
  opts.method = 'GET';
  request(opts, done);
}
function post(opts: any, done: any): void {
  opts.method = 'POST';
  request(opts, done);
}
function put(opts: any, done: any): void {
  opts.method = 'PUT';
  request(opts, done);
}
function del(opts: any, done: any): void {
  opts.method = 'DELETE';
  request(opts, done);
}
function request(opts: any, done: any): void {
  if (opts.url.indexOf('http') !== 0) {
    opts.url = getBaseUrl() + opts.url;
  }

  if (opts.retry) {
    _requestRetry(opts, done);
  } else {
    rawRequest(opts, done);
  }
}
function cdnGet(opts: any, done: any): void {
  opts.url = getCdnUrl() + opts.url;
  rawRequest(opts, done);
}

function _requestRetry(params: any, done: any): void {
  const opts = {
    times: params.retry_times ?? 5,
    interval: (count: number) => {
      const interval = Math.min(50 * Math.pow(3, count), MAX_RETRY_INTERVAL);
      return interval;
    },
    errorFilter: (err: any) => {
      let ret = true;
      if (typeof err === 'number' && err > 0 && err < 500) {
        ret = false;
      }
      return ret;
    },
  };
  asyncRetry(opts, (done: any) => rawRequest(params, done), done);
}

function rawRequest(args: any, done: any): void {
  function request_done(...done_extra: any[]): void {
    done(...done_extra);
    done = () => {};
  }

  const { method, track_progress } = args;

  let default_headers: any = {
    accept: 'application/json',
  };
  if (g_user_agent) {
    default_headers['user-agent'] = g_user_agent;
  }
  let body: any = null;
  if (args.body instanceof FormData) {
    body = args.body;
  } else if (args.body instanceof Blob) {
    body = args.body;
  } else if (args.body) {
    body = JSON.stringify(args.body);
    default_headers['content-type'] = 'application/json';
  }

  let url = args.url;
  if (args.query) {
    const list: string[] = [];
    for (let k in args.query) {
      const v = args.query[k];
      list.push(encodeURIComponent(k) + '=' + encodeURIComponent(v));
    }
    const query_s = list.join('&');
    if (query_s.length > 0) {
      if (url.indexOf('?') > 0) {
        url += '&' + query_s;
      } else {
        url += '?' + query_s;
      }
    }
  }

  const headers = Object.assign({}, default_headers, args.headers);

  const xhr = new XMLHttpRequest();
  if (args.timeout) {
    xhr.timeout = args.timeout;
  } else {
    xhr.timeout = DEFAULT_TIMEOUT;
  }

  xhr.onload = () => {
    let status = xhr.status === 1223 ? 204 : xhr.status;
    let response_body: any = false;
    let json = false;
    let err: any = null;

    response_body = xhr.response || xhr.responseText;
    const content_type = xhr.getResponseHeader('content-type');
    const is_json = content_type && content_type.indexOf('json') !== -1;

    if (response_body && response_body.length && is_json) {
      try {
        json = JSON.parse(response_body);
      } catch (e) {
        err = e;
      }
    }

    const header_text = xhr.getAllResponseHeaders();
    const header_list = header_text.split('\r\n');
    const response_headers: any = {};
    header_list
      .map((h) => h.split(': '))
      .forEach((h) => {
        response_headers[h[0].toLowerCase()] = h[1];
      });

    if (status < 100 || status > 599) {
      err = 'bad_status_code';
    } else if (status > 399) {
      err = status;
    }
    request_done(err, json || response_body, response_headers);
  };

  xhr.onerror = () => {
    request_done('request_failure');
  };
  xhr.ontimeout = () => {
    request_done('timeout');
  };

  xhr.open(method || 'GET', url, true);

  for (let name in headers) {
    const values = headers[name];
    if (Array.isArray(values)) {
      values.forEach((value) => {
        xhr.setRequestHeader(name, value);
      });
    } else {
      xhr.setRequestHeader(name, values);
    }
  }

  if (track_progress) {
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        track_progress((e.loaded / e.total) * 100);
      }
    };
  }

  xhr.send(body);
}
function newWebSocket(path: string, done: any): WebSocket {
  const http_url = getBaseUrl();
  let ws_url = http_url.replace('http', 'ws');

  if (path) {
    ws_url += path;
  }
  const ws = new WebSocket(ws_url);
  function _onError(err: any): void {
    done(err, ws);
    done = function () {};
  }
  function _onOpen(): void {
    ws.removeEventListener('error', _onError);
    ws.removeEventListener('close', _onClose);
    done(null, ws);
    done = function () {};
  }
  function _onClose(): void {
    done('close', ws);
    done = function () {};
  }
  if (done) {
    ws.addEventListener('error', _onError);
    ws.addEventListener('open', _onOpen);
    ws.addEventListener('close', _onClose);
  }
  return ws;
}
function _clean(s: string) {
  let ret = '';
  if (s) {
    ret = s.replace(/\s+/g, '_');
  }
  return ret;
}
